import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown, FaUsers, FaLock, FaCog, FaSave, FaUndo, FaSpinner } from 'react-icons/fa';
import {
  getLayoutConfigs,
  loadLayoutConfig,
  applyLayoutConfig,
  getCurrentLayoutState,
  saveLayoutConfig,
  getCurrentLayoutConfigId,
} from '../utils/layoutConfigStorage';
import { saveLayoutState, loadLayoutState } from '../utils/layoutStorage';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../utils/admin';
import SaveLayoutModal from './SaveLayoutModal';
import ManageLayoutsModal from './ManageLayoutsModal';

export default function LayoutDropdown({ onLayoutApplied, onResetLayout }) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [currentLayoutId, setCurrentLayoutId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const isAdmin = isAdminUser(user, userProfile);
  const userId = user?.uid;

  useEffect(() => {
    if (user) {
      loadUserProfile();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadLayouts();
      loadCurrentLayoutId();
    }
  }, [user, userProfile, isAdmin]);

  useEffect(() => {
    if (isOpen && user) {
      loadLayouts();
      loadCurrentLayoutId();
    }
  }, [isOpen, user, userProfile, isAdmin]);

  const loadUserProfile = async () => {
    if (!user?.uid) return;
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      const profileSnap = await getDoc(doc(db, 'users', user.uid));
      setUserProfile(profileSnap.exists() ? profileSnap.data() : null);
    } catch (error) {
      setUserProfile(null);
    }
  };

  const loadCurrentLayoutId = () => {
    const id = getCurrentLayoutConfigId();
    setCurrentLayoutId(id);
  };

  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (event) => {
        const inButton = buttonRef.current?.contains(event.target);
        const inMenu = menuRef.current?.contains(event.target);
        if (!inButton && !inMenu) setIsOpen(false);
      };
      const handleScrollOrResize = () => setIsOpen(false);
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) {
        setMenuPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
      }
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
      return () => {
        setMenuPosition(null);
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    } else {
      setMenuPosition(null);
    }
  }, [isOpen]);

  const loadLayouts = async () => {
    try {
      setLoading(true);
      const configs = await getLayoutConfigs(userId, isAdmin);
      setLayouts(configs);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load layouts:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLayout = async (layoutId) => {
    try {
      setApplyingId(layoutId);
      const layoutConfig = await loadLayoutConfig(layoutId);
      applyLayoutConfig(layoutConfig.layout_data, layoutId);
      setCurrentLayoutId(layoutId);

      if (onLayoutApplied) {
        onLayoutApplied();
      } else {
        window.location.reload();
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to apply layout:', error);
      }
      alert('Failed to apply layout: ' + error.message);
      setApplyingId(null);
    }
  };

  const handleSaveCurrentLayout = async ({ name, isPublic, isDefault, overwriteLayoutId }) => {
    try {
      const currentLayoutState = await getCurrentLayoutState();
      const savedConfig = await saveLayoutConfig({
        name,
        isPublic,
        isDefault: isAdmin ? isDefault : false,
        authorId: userId,
        layoutData: currentLayoutState,
        overwriteLayoutId: overwriteLayoutId || null,
      });
      await loadLayouts();
      setCurrentLayoutId(savedConfig.id);

      const existingState = loadLayoutState();
      saveLayoutState({
        ...(existingState || {}),
        ...currentLayoutState,
        currentRideViewLayoutConfigId: savedConfig.id,
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save layout:', error);
      }
      throw error;
    }
  };

  const handleResetLayout = () => {
    try {
      const currentState = loadLayoutState();
      if (currentState) {
        const { currentRideViewLayoutConfigId, ...stateWithoutId } = currentState;
        saveLayoutState(stateWithoutId);
      } else {
        localStorage.removeItem('currentRideViewLayoutConfigId');
      }
      setCurrentLayoutId(null);

      if (onResetLayout) {
        onResetLayout();
      } else {
        window.location.reload();
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to reset layout:', error);
      }
      setCurrentLayoutId(null);
      if (onResetLayout) onResetLayout();
      else window.location.reload();
    }
  };

  const currentLayout = layouts.find((l) => l.id === currentLayoutId);
  const buttonText = currentLayout ? `Layout: ${currentLayout.name}` : 'Layouts';

  const menuStyle = menuPosition ? {
    position: 'fixed',
    top: menuPosition.top,
    right: menuPosition.right,
    left: 'auto',
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 'var(--z-dropdown)',
    minWidth: '220px',
    maxHeight: '320px',
    overflowY: 'auto',
  } : null;

  return (
    <>
      <div>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="reset-layout-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          title="Layout configurations"
        >
          {buttonText}
          <FaChevronDown size={12} style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }} />
        </button>

        {isOpen && menuStyle && createPortal(
          <div ref={menuRef} style={menuStyle}>
            <div style={{ padding: '0.25rem' }}>
              {loading ? (
                <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center' }}>
                  <FaSpinner className="fa-spin" size={18} />
                </div>
              ) : layouts.length === 0 ? (
                <div style={{ padding: '1rem', fontSize: '0.875rem', color: '#666', textAlign: 'center' }}>
                  No saved layouts
                </div>
              ) : (
                layouts.map((layout) => {
                  const isCurrentLayout = layout.id === currentLayoutId;
                  return (
                    <button
                      key={layout.id}
                      type="button"
                      onClick={() => {
                        setIsOpen(false);
                        handleApplyLayout(layout.id);
                      }}
                      disabled={applyingId === layout.id}
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        border: 'none',
                        background: isCurrentLayout ? '#f5f5f5' : 'transparent',
                        cursor: applyingId === layout.id ? 'wait' : 'pointer',
                        fontSize: '0.875rem',
                      }}
                    >
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {layout.name}
                        {layout.is_default && ' *'}
                      </span>
                      {layout.is_public ? (
                        <FaUsers size={12} title="Public" />
                      ) : (
                        <FaLock size={12} title="Private" />
                      )}
                      {applyingId === layout.id && <FaSpinner className="fa-spin" size={12} />}
                    </button>
                  );
                })
              )}

              {layouts.length > 0 && (
                <div style={{ borderTop: '1px solid #e0e0e0', margin: '0.25rem 0' }} />
              )}

              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setShowManageModal(true);
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                <FaCog size={14} />
                Manage Layouts
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  handleResetLayout();
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                <FaUndo size={14} />
                Reset to Default
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setShowSaveModal(true);
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                <FaSave size={14} />
                Save Custom Layout...
              </button>
            </div>
          </div>,
          document.body
        )}
      </div>

      <SaveLayoutModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveCurrentLayout}
        isAdmin={isAdmin}
        existingLayouts={layouts}
      />

      <ManageLayoutsModal
        isOpen={showManageModal}
        onClose={() => setShowManageModal(false)}
        onLayoutsChange={() => {
          loadLayouts();
          loadCurrentLayoutId();
        }}
      />
    </>
  );
}
